import { CommandProps, Extension } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { Node } from '@tiptap/pm/model';

// Utils
import { isTiptapEditorEmpty } from '../utils/isTiptapEditorEmpty';
import {
    hasNestedListContent,
    LIST_CONTAINER_TYPES,
    shouldLiftListItem,
    findListContext,
} from './list/tiptapListUtils';
import { safeDelete } from '../utils/transactionUtils';

// Types
import { TiptapNodeType } from '../tiptapTypes';

const WARNING_MESSAGE = `Tiptap.EmptyDelete: The Milanote "EmptyDelete" extension was triggered without a configured handler.

    When using the EmptyDelete extension, it should be configured with an onEmptyDelete handler. E.g:
    
    EmptyDelete.configure({
        onEmptyDelete: () => {
            console.log('Handling onEmptyDelete');
        },
    });
`;

const WrappingBlockTypes = new Set<string>([TiptapNodeType.blockquote, TiptapNodeType.callout]);

enum BackspaceOperation {
    ignore = 0,
    convertToParagraph = 'convertToParagraph',
    joinToWrappingBlock = 'joinToWrappingBlock',
    liftList = 'liftList',
    joinBackward = 'joinBackward',
}

/**
 * Finds the position where a previous text node ends, starting from a given position.
 */
const findPreviousTextNodeEnd = ($doc: Node, startPos: number): number | null => {
    try {
        let $pos = $doc.resolve(startPos);

        while ($pos.pos > 0) {
            // If we're at a text block, we've found it
            if ($pos.parent.isTextblock) return $pos.end();

            // Move to the previous position
            $pos = $doc.resolve($pos.pos - 1);
        }
    } catch (error) {
        console.error('Error finding previous text node end:', error);
    }

    return null;
};

/**
 * Determine the operation to perform when the backspace key is pressed.
 */
const getBackspaceOperation = (commandProps: CommandProps): BackspaceOperation => {
    const { tr, state } = commandProps;
    const { empty, $from } = tr.selection;

    // If the selection isn't empty, we let the standard backspace handler remove the text
    if (!empty) return BackspaceOperation.ignore;

    // If not at the start of the line, let standard handler work
    if ($from.parentOffset !== 0) return BackspaceOperation.ignore;

    // Get current node
    const topLevelNode = $from.node(1);

    // Handle list items with improved logic
    const listContext = findListContext(state, tr.selection);
    if (listContext) {
        const { listItem: listItemNode, parentList: listContainerNode, listItemIndex } = listContext;

        // Check if we should lift the list item (e.g., reduce indentation)
        const shouldLift = shouldLiftListItem({
            listItemNode,
            parentListNode: listContainerNode,
            indexInParent: listItemIndex,
            editorState: state,
        });

        if (shouldLift) return BackspaceOperation.liftList;

        // If this list item has nested content and we're not lifting,
        // avoid joining to prevent numbering issues
        if (listItemIndex > 0 && hasNestedListContent(listItemNode)) {
            return BackspaceOperation.ignore; // Let default handler deal with it
        }

        // If we're the first list item, there's no previous item to join to
        if (listItemIndex <= 0) return BackspaceOperation.ignore;

        // We have a previous list item to join with
        return BackspaceOperation.joinBackward;
    }

    // Handle paragraphs that might join to blockquotes or other wrapping blocks (e.g. callouts)
    if (topLevelNode.type === state.schema.nodes.paragraph) {
        // The node that we'll be moving into
        const $targetPos = state.doc.resolve(Math.max($from.pos - 2, 1));

        return WrappingBlockTypes.has($targetPos.node().type.name)
            ? BackspaceOperation.joinToWrappingBlock
            : BackspaceOperation.ignore;
    }

    // If we're at the start of a paragraph in a blockquote, or callout, use the default backspace
    //  behaviour, which will simply remove join to the previous paragraph in the blockquote or callout, if there
    //  is one, or remove the blockquote or callout if there isn't one.
    if (topLevelNode.type === state.schema.nodes.blockquote || topLevelNode.type === state.schema.nodes.callout) {
        return BackspaceOperation.ignore;
    }

    // For other block-level nodes that aren't lists, convert to paragraph
    if (!LIST_CONTAINER_TYPES.has(topLevelNode.type.name as TiptapNodeType)) {
        return BackspaceOperation.convertToParagraph;
    }

    // If we reach here, we have a list container but no valid list context
    // This is likely a malformed structure - let standard handler deal with it
    return BackspaceOperation.ignore;
};

/**
 * Handle joining the current list item with the previous one
 * Preserves custom marks while maintaining proper list structure
 * Scans backwards to find meaningful content to join with,
 * using ProseMirror's position resolution for safe document navigation
 */
const handleJoinBackward = (commandProps: CommandProps): boolean => {
    const { tr, state } = commandProps;
    const { $from } = tr.selection;

    const listContext = findListContext(state, tr.selection);
    // No valid list context or no previous item to join
    if (!listContext) return false;

    const { listItemDepth, listItemIndex } = listContext;

    // If we're the first list item, there's no previous item to join with
    if (listItemIndex <= 0) return false;

    try {
        const currentListItemStart = $from.before(listItemDepth);
        const currentCursorPosition = $from.pos;
        const previousTextNodeEnd = findPreviousTextNodeEnd(tr.doc, currentListItemStart);

        if (!previousTextNodeEnd) return false;

        return safeDelete(tr, previousTextNodeEnd, currentCursorPosition);
    } catch (error) {
        console.error('Error in handleJoinBackward:', error);
        return false;
    }
};

/**
 * Perform a specific backspace operation.
 */
const performBackspaceOperation = (operation: BackspaceOperation, commandProps: CommandProps) => {
    const { dispatch, commands, tr } = commandProps;

    if (!operation) return false;
    if (!dispatch) return true;

    switch (operation) {
        case BackspaceOperation.convertToParagraph: {
            commands.setParagraph();
            return true;
        }
        case BackspaceOperation.joinToWrappingBlock: {
            // Connect the text in this paragraph to the text in the previous wrapping block's paragraph
            tr.delete(
                // Logic is, remove from:
                //  - past the start of this paragraph's node (-1)
                //  - past the closing wrapping block node (-1)
                //  - past the closing paragraph node (-1)
                tr.selection.$from.pos - 3,
                // to:
                //  - the start of this paragraph's text
                tr.selection.$from.pos,
            );

            return true;
        }
        case BackspaceOperation.liftList: {
            // Find the list item node in the hierarchy
            const { $from } = tr.selection;
            let listItemNode = null;
            let listItemType = null;

            for (let depth = $from.depth; depth >= 0; depth--) {
                const node = $from.node(depth);
                if (node.type.name === TiptapNodeType.listItem) {
                    listItemNode = node;
                    listItemType = node.type;
                    break;
                }
            }

            if (!listItemNode || !listItemType) return false;

            commands.liftListItem(listItemType);
            return true;
        }
        case BackspaceOperation.joinBackward: {
            return handleJoinBackward(commandProps);
        }
    }
};

export interface EmptyDeleteOptions {
    /**
     * The function that will get invoked if the editor is empty
     * when the "delete" or "backspace" key is pressed.
     *
     * It should return `true` if it's handled, `false` otherwise.
     */
    onEmptyDelete: (editor: Editor) => boolean;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emptyDelete: {
            /**
             * Check if editor is empty and invoke delete handler
             * @example editor.commands.checkIfEmptyDelete()
             */
            checkIfEmptyDelete: () => ReturnType;
            /**
             * Handle backspace at start of line with custom logic
             * @example editor.commands.handleBackspaceAtStartOfLine()
             */
            handleBackspaceAtStartOfLine: () => ReturnType;
        };
    }
}

export const EmptyDelete = Extension.create<EmptyDeleteOptions>({
    name: 'emptyDelete',

    addOptions() {
        return {
            onEmptyDelete: (editor: Editor) => {
                console.warn(WARNING_MESSAGE);
                return false;
            },
        };
    },

    addCommands() {
        return {
            checkIfEmptyDelete:
                () =>
                ({ editor }) => {
                    const { onEmptyDelete } = this.options;

                    if (!isTiptapEditorEmpty(editor)) return false;

                    return onEmptyDelete(editor);
                },
            handleBackspaceAtStartOfLine: () => (commandProps) => {
                const operation = getBackspaceOperation(commandProps);
                return performBackspaceOperation(operation, commandProps);
            },
        };
    },

    addKeyboardShortcuts() {
        const onBackspace = () =>
            this.editor.commands.first(({ commands }) => {
                const commandList = [
                    // Try custom backspace handling first (this now handles all the logic internally)
                    commands.handleBackspaceAtStartOfLine,
                    // Fall back to empty delete check
                    commands.checkIfEmptyDelete,
                ];
                return commandList.filter((cmd) => typeof cmd === 'function');
            });

        const onDelete = () => this.editor.commands.checkIfEmptyDelete();

        const backspaceHandlers = [
            'Backspace',
            'Shift-Backspace',
            'Alt-Backspace',
            'Control-Backspace',
            'Cmd-Backspace',
        ].map((shortcut): [string, () => boolean] => [shortcut, onBackspace]);

        const deleteHandlers = ['Delete', 'Shift-Delete', 'Alt-Delete', 'Control-Delete', 'Cmd-Delete'].map(
            (shortcut): [string, () => boolean] => [shortcut, onDelete],
        );

        return Object.fromEntries([...backspaceHandlers, ...deleteHandlers]);
    },
});
